'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CheckCircle,
  WarningAmber,
  ArrowForward,
  ExpandMore,
  FolderOpen,
  Archive,
  AccountTree,
  UploadFile,
  Search,
  Print,
  CompareArrows,
  History,
  Domain,
  VerifiedUser,
  FileDownload
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import Footer from '../../components/HomepageContent/Footer';
import Link from 'next/link';
import '../features-responsive.css';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const workflowSteps = ['Generate Report', 'Auto-Archive', 'Search & Filter', 'Preview & Compare', 'Export for Survey'];

const painPoints = [
  'Finalized reports buried in email, personal drives, or paper files',
  'No centralized view of grievance, meeting, and investigation documentation',
  'Hard to prove document history during surveys or corporate audits',
  'External compliance documents managed separately from operational records',
  'Time wasted searching across modules when leadership or surveyors need proof',
  'Inconsistent document organization across facilities in a multi-site organization',
];

const capabilities = [
  {
    icon: FolderOpen,
    title: 'Unified Document Repository',
    description: 'Store all facility documentation in one secure vault—organized by context so teams always know where to look.',
  },
  {
    icon: Archive,
    title: 'Automatic Report Archiving',
    description: 'When grievance reports, committee meeting minutes, or incident investigation reports are finalized, they are saved directly to DocVault—no manual filing required.',
  },
  {
    icon: AccountTree,
    title: 'Context-Based Organization',
    description: 'Browse documents by module context—Grievance, Committee Meeting, Incident Investigation, and External—so the right records are always one click away.',
  },
  {
    icon: UploadFile,
    title: 'External Document Management',
    description: 'Upload and manage external compliance documents (PDF, Word, Excel, images) with document types, metadata, and full edit and delete controls where permitted.',
  },
  {
    icon: Search,
    title: 'Advanced Search & Filtering',
    description: 'Search by document name, creator, or modifier. Filter by year, month, document type, and more to find records in seconds—not hours.',
  },
  {
    icon: Print,
    title: 'In-App Preview & Print',
    description: 'View documents directly in the platform. Preview grievance reports, committee meeting PDFs, and investigation reports without downloading files to personal devices.',
  },
  {
    icon: CompareArrows,
    title: 'Meeting Report Comparison',
    description: 'Compare current and previous committee meeting reports side by side to see what changed—useful for audit trails and leadership review.',
  },
  {
    icon: History,
    title: 'Complete Document History',
    description: 'Track every add, update, delete, print, and download action with timestamps and user attribution for a full audit trail.',
  },
  {
    icon: Domain,
    title: 'Facility-Level Security',
    description: 'Documents are scoped to the selected facility, supporting both single-site operations and enterprise-wide document governance.',
  },
  {
    icon: VerifiedUser,
    title: 'Survey-Ready Exports',
    description: 'Generate and print professional PDF reports on demand—ready when surveyors, corporate leadership, or legal counsel ask for documentation.',
  },
];

const roles = [
  {
    title: 'Administrators & DONs',
    benefit: 'One place to find every finalized quality document for the facility',
  },
  {
    title: 'QAPI & Compliance Leaders',
    benefit: 'Survey-ready evidence with searchable history and audit trails',
  },
  {
    title: 'Committee Chairs & Recorders',
    benefit: 'Meeting reports archived automatically—no manual filing',
  },
  {
    title: 'Investigation & Grievance Staff',
    benefit: 'Finalized reports stored and retrievable without reconstructing timelines',
  },
  {
    title: 'Corporate / Multi-Site Operators',
    benefit: 'Consistent document vault structure across every facility',
  },
];

const outcomes = [
  { value: '1', label: 'Source of truth', detail: 'All finalized quality documents in one vault' },
  { value: 'Fast', label: 'Document retrieval', detail: 'Search and filter vs. hunting through email and drives' },
  { value: '100%', label: 'Audit trail', detail: 'Full history of views, prints, and changes' },
  { value: 'Zero', label: 'Manual filing', detail: 'Auto-archived reports from connected modules' },
];

const integrations = [
  'Grievance Management',
  'Committee & Meeting Management',
  'Incident Investigation',
  'Policy & Procedure Management',
  'QAPI',
  'External Document Upload',
];

const faqs = [
  {
    q: 'What documents are stored automatically in DocVault?',
    a: 'Finalized reports from Grievance Management, Committee & Meeting Management, and Incident Investigation are archived to DocVault when saved—organized by their source module.',
  },
  {
    q: 'Can we upload documents from outside the platform?',
    a: 'Yes. The External context supports uploading PDF, Word, Excel, and image files with document type classification and metadata.',
  },
  {
    q: 'Can we see who accessed or changed a document?',
    a: 'Yes. DocVault maintains a complete history log including adds, updates, deletes, prints, and downloads.',
  },
  {
    q: 'Can we compare versions of committee meeting reports?',
    a: 'Yes. DocVault supports side-by-side comparison of current and previous committee meeting reports to highlight changes.',
  },
  {
    q: 'Is DocVault available per facility?',
    a: 'Yes. Documents are organized by facility, with context tabs within each facility’s vault.',
  },
];

export default function DocumentVaultClient() {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    document.title = 'Document Vault & Quality Record Storage | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 6, md: 8 },
          px: 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="DocVault · Document Vault"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              maxWidth: 940,
              mx: 'auto',
            }}
          >
            One Secure Vault for Every Survey-Ready Document Across Your Quality Program
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 840,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
            }}
          >
            CareAscendium DocVault automatically archives finalized reports from grievances, committee meetings, and incident investigations—plus stores external compliance documents—in a searchable, facility-level document repository with version history, preview, and print-ready exports.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>

          </Box>
        </Container>
      </Box>

      {/* Problem */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={5} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              The Challenge
            </Typography>
            <Typography sx={{ color: '#666', mb: 3, lineHeight: 1.7, fontSize: '1.05rem' }}>
              In most SNFs, critical documentation lives everywhere: shared drives, email attachments, binders, and individual staff folders. When a surveyor asks for grievance records, committee minutes, or investigation reports, teams scramble to find the right version. External documents—policies, contracts, regulatory filings—sit outside the quality workflow entirely. There is no single place to search, filter, and prove what was documented and when.
            </Typography>
            <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0 }}>
              {painPoints.map((point) => (
                <Box
                  component="li"
                  key={point}
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1.5 }}
                >
                  <WarningAmber sx={{ color: '#FF9800', mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ color: '#555', lineHeight: 1.5 }}>{point}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #F3F1FF 0%, #ffffff 100%)',
                borderLeft: `4px solid ${BRAND_BLUE}`,
              }}
            >
              <Typography
                sx={{
                  fontStyle: 'italic',
                  color: BRAND_BLUE,
                  fontSize: '1.15rem',
                  lineHeight: 1.6,
                  fontWeight: 500,
                }}
              >
                &ldquo;A document isn't helpful if you can't find it when a surveyor is sitting at the table. Every finalized report needs one secure home.&rdquo;
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Solution / Workflow */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
              Unified Quality Record Storage
            </Typography>
            <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem' }}>
              CareAscendium DocVault removes the manual filing burden. The moment a grievance, meeting, or investigation is finalized, its report auto-archives securely. Combined with powerful tools to upload external files, search across modules, and compare versions, you get total visibility into facility records.
            </Typography>
          </Box>
          <Paper elevation={2} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: 'white' }}>
            <Stepper
              alternativeLabel
              activeStep={-1}
              sx={{
                '& .MuiStepLabel-label': { fontWeight: 600, color: BRAND_BLUE, mt: 1 },
                '& .MuiStepIcon-root': { color: BRAND_BLUE, '&.Mui-completed': { color: BRAND_GREEN } },
              }}
            >
              {workflowSteps.map((label) => (
                <Step key={label} completed>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 4, flexWrap: 'wrap', textAlign: 'center' }}>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', maxWidth: 200 }}>
                 <b>Generate Report:</b> Finalize records in connected modules
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', maxWidth: 200 }}>
                 <b>Auto-Archive:</b> Save seamlessly to the vault
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', maxWidth: 200 }}>
                 <b>Search & Filter:</b> Locate documents in seconds
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', maxWidth: 200 }}>
                 <b>Preview & Compare:</b> View side-by-side differences online
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', maxWidth: 200 }}>
                 <b>Export for Survey:</b> Print professional PDFs instantly
               </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Capabilities */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Key Capabilities
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          {capabilities.map((cap) => (
            <Box key={cap.title}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 28px rgba(21,60,127,0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    bgcolor: `${BRAND_BLUE}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
                </Box>
                <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 1.5 }}>
                  {cap.title}
                </Typography>
                <Typography sx={{ color: '#666', mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {cap.description}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Roles + Integration */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} justifyContent="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: '1.75rem' }}>
                Who Benefits
              </Typography>
              {roles.map((role) => (
                <Box key={role.title} sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: BRAND_BLUE }}>{role.title}</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {role.benefit}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  height: '100%',
                  background: `linear-gradient(145deg, ${BRAND_BLUE} 0%, #1E4A8C 100%)`,
                  color: 'white',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: '1.5rem' }}>
                  Part of the CareAscendium Platform
                </Typography>
                <Typography sx={{ opacity: 0.9, mb: 3, lineHeight: 1.6 }}>
                  DocVault acts as the ultimate destination for generated quality records from across the suite:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {integrations.map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.25)',
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontWeight: 600, color: '#a8d86a', fontStyle: 'italic' }}>
                  One platform. One source of truth. Always audit-ready.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Outcomes & Compliance */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Outcomes
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 3,
            mb: 8,
            alignItems: 'stretch',
            maxWidth: 1000,
            mx: 'auto'
          }}
        >
          {outcomes.map((item) => (
            <Paper
              key={item.label}
              elevation={2}
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: BRAND_GREEN, mb: 0.5 }}>
                {item.value}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, mb: 0.5, fontSize: '0.95rem' }}>
                {item.label}
              </Typography>
              <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>{item.detail}</Typography>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* FAQ */}
      <Box sx={{ py: { xs: 10, md: 14 }, px: 4, background: 'linear-gradient(113.99deg, #153C7F 12.55%, #000820 87.43%)' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            FAQs
          </Typography>
          <Box>
            {faqs.map((faq, i) => (
              <Accordion 
                key={i}
                expanded={expanded === i} 
                onChange={handleChange(i)}
                sx={{ 
                  mb: 2.5, 
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px !important',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)'
                  },
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white' }} />} sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.02em' }}>{faq.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.8, fontSize: '1.05rem' }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 6, bgcolor: '#f0f4fa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.5rem' }, textTransform: 'uppercase' }}>
              Stop Searching for Files
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6, fontSize: '1.1rem', maxWidth: 850 }}>
              Ensure every finalized grievance, meeting report, and incident investigation is securely archived and ready for survey.
            </Typography>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '30px',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: '#528a1b' },
                  boxShadow: '0 4px 10px rgba(99, 168, 33, 0.2)',
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
